/**
 * CORE-882 — что можно сделать с проданной программой и как об этом сказать.
 *
 * Модуль без зависимостей от React Native: здесь решения, и цена ошибки в них
 * выше обычной — речь о деньгах и о доступе, за который человек заплатил.
 *
 * Правило, проходящее через весь модуль: **предупреждение обязано называть
 * число людей**. «У программы есть покупатели» и «программу купили 47 человек»
 * читаются по-разному, и второе останавливает вовремя.
 */
import { deleteForeverSentence } from './programDeleteCopy';
import type { MasterProgram } from '../../../types/programs';
import type {
  ProgramLifecyclePreflight,
  ProgramRefundRequest,
  RefundProviderStatus,
  RefundStatus,
} from '../../../types/programLifecycle';

// ─── Заявка на возврат глазами покупателя ───

/** Подпись статуса заявки. Наши слова, не провайдера. */
export function refundStatusLabel(status: RefundStatus): string {
  switch (status) {
    case 'requested': return 'Заявка отправлена';
    case 'underReview': return 'Заявку рассматривают';
    case 'approved': return 'Возврат одобрен';
    case 'rejected': return 'В возврате отказано';
    case 'refunded': return 'Деньги возвращены';
    default: return 'Заявка на возврат';
  }
}

/**
 * Что происходит дальше.
 *
 * Сроков здесь нет ни в одном состоянии: заявки разбирают люди, а обещать
 * чужое время нельзя. Человеку важнее знать, что заявку видят.
 */
export function refundStatusNote(request: ProgramRefundRequest): string {
  switch (request.status) {
    case 'requested':
      return 'Мы получили заявку и скоро её посмотрим. Доступ пока сохраняется.';
    case 'underReview':
      return 'Заявку взяли в работу. Доступ пока сохраняется.';
    case 'approved':
      return 'Возврат одобрен, деньги в пути. Доступ к программе закрыт.';
    case 'rejected':
      // Причина отказа обязательна на стороне сервера — показываем её, а не
      // свой текст: объяснение принадлежит тому, кто принял решение.
      return request.decisionNote || 'Покупка и доступ остаются как были.';
    case 'refunded':
      return 'Деньги вернулись. Покупка осталась в истории.';
    default:
      return '';
  }
}

/**
 * Статус перевода — только когда он что-то добавляет.
 *
 * Пока решения нет, перевода нет тоже, и строка «перевод: неизвестно» лишь
 * тревожит. Появляется, когда есть что сказать.
 */
export function providerStatusNote(status: RefundProviderStatus | null): string {
  switch (status) {
    case 'pending': return 'Перевод в обработке';
    case 'succeeded': return 'Перевод прошёл';
    case 'failed': return 'Перевод не прошёл — мы разбираемся';
    case 'canceled': return 'Перевод отменён';
    default: return '';
  }
}

/** Заявка закрыта — по ней больше ничего не произойдёт. */
export function isRefundClosed(status: RefundStatus): boolean {
  return status === 'rejected' || status === 'refunded';
}

/**
 * CORE-880 (QA FAIL 08.09) — по покупке берётся ПОСЛЕДНЯЯ заявка на возврат.
 *
 * В отчёте одна карточка показывала сразу «Деньги возвращены — доступ закрыт» и
 * «В возврате отказано» со старой причиной. У покупки 19 было две заявки:
 * отклонённая (старая) и возвращённая (новая), а интерфейс показывал решение по
 * старой.
 *
 * Причина — сборка через `Object.fromEntries`: при совпадении ключей выигрывает
 * ПОСЛЕДНЯЯ пара, а список приходит новыми сверху. То есть побеждала самая
 * старая заявка по каждой покупке — ровно наоборот.
 *
 * Порядок выдачи здесь не используется намеренно: сортировку сервера легко
 * поменять, не заметив этого места. Свежесть определяется по времени подачи, а
 * при совпадении — по идентификатору: он монотонный, и это разводит заявки,
 * поданные в одну секунду.
 *
 * «Последняя» — не то же самое, что «успешная»: если после возврата человек
 * подаст новую заявку, показать надо её, а не прошлый исход. Основное состояние
 * покупки при этом живёт в самой покупке (`paymentStatus`), и этот блок его
 * дополняет, а не заменяет.
 */
export function latestRefundByPurchase(
  requests: readonly ProgramRefundRequest[] | null | undefined,
): Record<number, ProgramRefundRequest> {
  const byPurchase: Record<number, ProgramRefundRequest> = {};
  for (const request of requests ?? []) {
    if (!request || typeof request.purchaseId !== 'number') continue;
    const current = byPurchase[request.purchaseId];
    if (!current || isNewerRefund(request, current)) {
      byPurchase[request.purchaseId] = request;
    }
  }
  return byPurchase;
}

/** Подана позже — или в то же время, но с большим идентификатором. */
function isNewerRefund(a: ProgramRefundRequest, b: ProgramRefundRequest): boolean {
  const at = Date.parse(a.createdAtUtc ?? '');
  const bt = Date.parse(b.createdAtUtc ?? '');
  // Непарсимая дата не должна вытеснять годную: тогда решает идентификатор.
  if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at > bt;
  return (a.id ?? 0) > (b.id ?? 0);
}

/**
 * Можно ли просить возврат по этой покупке.
 *
 * Просить можно только по оплаченной и только один раз за раз: незакрытая
 * заявка делает вторую бессмысленной, а деньги за возвращённое не возвращают
 * дважды.
 */
export function canRequestRefund(
  purchase: { paymentStatus: string; refundBlockedCode?: string | null },
  openRequest: ProgramRefundRequest | null,
): boolean {
  if (purchase.paymentStatus !== 'paid') return false;
  if (openRequest != null && !isRefundClosed(openRequest.status)) return false;
  // CORE-2071: правило возврата сервер уже применил к этой покупке.
  return !purchase.refundBlockedCode;
}

/** CORE-2071 — коды, которыми сервер закрывает возврат продукта мастера. */
export const REFUND_BLOCK_TEXT: Record<string, string> = {
  REFUND_PRODUCT_USED:
    'Вы уже начали заниматься по этой покупке, поэтому вернуть деньги нельзя. Возврат возможен, пока материалы не открывались.',
  REFUND_EVENT_STARTED:
    'Событие уже началось, поэтому вернуть деньги нельзя. Возврат за вебинар или событие возможен до его начала.',
};

/**
 * CORE-2071 — почему кнопки «Вернуть деньги» нет. Пусто — объяснять нечего:
 * либо возврат доступен, либо покупка не оплачена, либо заявка уже идёт и
 * про неё говорит её собственный статус.
 */
export function refundBlockedReason(
  purchase: { paymentStatus: string; refundBlockedCode?: string | null; refundBlockedMessage?: string | null },
  openRequest: ProgramRefundRequest | null,
): string {
  if (purchase.paymentStatus !== 'paid' || !purchase.refundBlockedCode) return '';
  if (openRequest != null && !isRefundClosed(openRequest.status)) return '';
  return REFUND_BLOCK_TEXT[purchase.refundBlockedCode] ?? purchase.refundBlockedMessage ?? '';
}

// ─── Программа глазами мастера ───

export type LifecycleAction = 'pauseSales' | 'resumeSales' | 'archive' | 'unarchive' | 'cancel' | 'delete';

export interface LifecycleOption {
  action: LifecycleAction;
  label: string;
  /** Действие меняет чужой доступ или деньги — такое подтверждают. */
  destructive: boolean;
}

/**
 * Что мастер может сделать с программой прямо сейчас.
 *
 * Список строится из ответа сервера, а не из догадок: предложить действие,
 * которое сервер отвергнет, — это обещание, которое интерфейс не выполнит.
 */
export function availableActions(p: ProgramLifecyclePreflight): LifecycleOption[] {
  if (p.canceled) return [];

  const options: LifecycleOption[] = [];

  if (p.status === 'published') {
    options.push(p.salesPaused
      ? { action: 'resumeSales', label: 'Вернуть продажи', destructive: false }
      : { action: 'pauseSales', label: 'Приостановить продажи', destructive: false });
  }

  if (p.canArchive) options.push({ action: 'archive', label: 'Убрать в архив', destructive: false });
  if (p.status === 'archived') options.push({ action: 'unarchive', label: 'Вернуть из архива', destructive: false });

  if (p.canHardDelete) {
    options.push({ action: 'delete', label: 'Удалить', destructive: true });
  } else if (p.status !== 'draft') {
    // Удалять нечего — но отменить можно, и это другой сценарий: с
    // предупреждением, причиной и уведомлением покупателей.
    options.push({ action: 'cancel', label: 'Отменить программу', destructive: true });
  }

  return options;
}

/**
 * Предупреждение перед действием.
 *
 * Называет число людей, а не факт их наличия: «программу купили 47 человек»
 * останавливает вовремя, «есть покупатели» — нет. Пустая строка значит, что
 * предупреждать не о чем.
 */
export function actionWarning(
  action: LifecycleAction,
  p: ProgramLifecyclePreflight,
  program?: Pick<MasterProgram, 'title' | 'productType'> | null,
): string {
  switch (action) {
    case 'pauseSales':
      return p.activeAccessCount > 0
        ? `Новых покупок не будет. ${plural(p.activeAccessCount, 'человек продолжит', 'человека продолжат', 'человек продолжат')} заниматься — доступ и Live-сессии останутся на месте.`
        : 'Новых покупок не будет. Страница программы останется открытой.';

    case 'archive':
      return p.buyerCount > 0
        ? `Программа уйдёт из публичной выдачи и перестанет продаваться. ${plural(p.buyerCount, 'купивший', 'купивших', 'купивших')} сохранит доступ до конца своего срока, история покупок останется.`
        : 'Программа уйдёт из публичной выдачи и перестанет продаваться.';

    case 'cancel':
      return p.buyerCount > 0
        ? `Программу купили ${plural(p.buyerCount, 'человек', 'человека', 'человек')}. Все они получат уведомление с вашим объяснением. Деньги автоматически не возвращаются — правила возврата ещё не утверждены, и каждый покупатель может подать заявку сам.`
        : 'Программа будет отменена. Вернуть её из отмены нельзя.';

    case 'delete':
      // Та же фраза, что и у корзины на карточке: два подтверждения одного
      // нажатия не имеют права описывать его по-разному. Без программы
      // (её не передали) остаётся общее — но без слова «черновик».
      return program
        ? deleteForeverSentence(program, p.status)
        : 'Продукт и весь его состав пропадут навсегда. Вернуть будет нечем.';

    default:
      return '';
  }
}

/** Нужна ли причина. У отмены — да: текст уходит покупателям. */
export function requiresReason(action: LifecycleAction): boolean {
  return action === 'cancel';
}

/** Состояние продаж одной строкой — для карточки в «Созданном». */
export function salesStateLabel(p: {
  status: string; salesPaused: boolean; canceled: boolean;
}): string {
  if (p.canceled) return 'Отменена';
  if (p.status === 'archived') return 'В архиве';
  if (p.salesPaused) return 'Продажи приостановлены';
  return '';
}

/**
 * Что сказать после отмены.
 *
 * Число уведомлённых — не украшение: мастер должен видеть, что его объяснение
 * действительно дошло, а не растворилось.
 */
export function cancelResultMessage(result: {
  notifiedBuyerCount: number; refundsCreated: boolean;
}): string {
  if (result.notifiedBuyerCount === 0) return 'Программа отменена.';

  const notified = `Программа отменена, ${plural(
    result.notifiedBuyerCount, 'покупатель получил', 'покупателя получили', 'покупателей получили',
  )} уведомление.`;

  return result.refundsCreated
    ? notified
    : `${notified} Возвраты не создавались — каждый может подать заявку сам.`;
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = (mod10 === 1 && mod100 !== 11) ? one
    : (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) ? few
      : many;
  return `${n} ${word}`;
}
